// =============================================================================
// Copyright   : 2026 semantics GmbH
// Date        : $Date: 2026-09-09 16:55:15 +0200 (Wed, 09 Sep 2026) $
// Author      : Dirk Rothe, Olli Powroznik
// Worker      : $Author: o.powroznik $
// Revision    : $Rev: 165402 $
// Purpose     : hooks for presentation module
// =============================================================================
// ---Third-party imports
import { isNil } from 'ldash';
// ---Framework imports
import log from 'uix/logging';
export const blurPercentToPx = (percent, max = 30)=>`${parseFloat(percent) / 100 * max}px`;
export const scalePercentToDeci = (percent, base = 0)=>`${parseFloat(percent) / 100 + base}`;
export const validateField = (val, spec)=>{
    if (isNil(val) || val === '') return {
        isValid: true
    };
    let isValid = true;
    let errorMsg = null;
    if (spec.proptype === 'percentage') {
        let percentageRegex = /^-?\d*\.?\d+%?$/;
        if (typeof val === 'string' && !percentageRegex.test(val.trim()) || typeof val !== 'string' && typeof val !== 'number') {
            isValid = false;
            errorMsg = 'Erwartet Prozentwert (z.B. "50", "50%").';
        }
    } else if (spec.proptype === 'cssdim') {
        let cssDimRegex = /^(?:-?\d*\.?\d+)(px|pt|em|rem|lh|vh|vw|vmin|vmax|dvh|dvw|%)?$|^auto$|^inherit$|^initial$|^unset$|^max-content$|^min-content$|^fit-content$/i;
        if (typeof val === 'string' && !cssDimRegex.test(val.trim()) || typeof val !== 'string' && typeof val !== 'number') {
            isValid = false;
            errorMsg = 'Erwartet CSS Dimension (z.B. "10px", "2em", "auto").';
        }
    } else if (spec.proptype === 'duration') {
        let durationRegex = /^\d*\.?\d+(s|ms)?$/i;
        if (typeof val === 'string' && !durationRegex.test(val.trim()) || typeof val !== 'string' && (typeof val !== 'number' || val < 0)) {
            isValid = false;
            errorMsg = 'Erwartet positive Dauer (z.B. "1.5", "1.5s").';
        }
    } else if (spec.type === 'color') {
        if (typeof val !== 'string') {
            isValid = false;
            errorMsg = 'Erwartet Farbwert.';
        } else if (typeof document !== 'undefined') {
            // Echte Browser-Validierung analog zu parseColor:
            let el = document.createElement('div');
            el.style.color = val;
            if (el.style.color === '') {
                isValid = false;
                errorMsg = 'Erwartet gültigen Farbwert (z.B. "#fff", "rgb(0,0,0)", "transparent").';
            }
        }
    } else if (spec.type === 'int') {
        let n = Number(val);
        if (isNaN(n) || !Number.isInteger(n)) {
            isValid = false;
            errorMsg = 'Erwartet ganze Zahl (Integer).';
        }
    } else if (spec.type === 'float' || spec.type === 'number') {
        if (isNaN(Number(val))) {
            isValid = false;
            errorMsg = 'Erwartet Zahl (Float).';
        }
    }
    return {
        isValid,
        errorMsg
    };
};
// faulty meta properties collected by validateMeta, listed by the debug menu (MetaErrorList)
export const metaErrors = new Map() // `${typeName}|${id}|${field}` -> error
;
//!TODO: überprüfen ob noch was nach validateField kann, validateMeta -> normalizeMeta umbennenen
export const validateMeta = (typeName, meta, schema = null, otid)=>{
    const id = meta.id || otid || meta.refid || null;
    const scope = `${typeName}|${id ?? 'unknown'}`;
    // a revalidation (value fixed in the property editor) drops its stale entries
    for (let key of [
        ...metaErrors.keys()
    ])if (key.startsWith(`${scope}|`)) metaErrors.delete(key);
    const handleError = (message, field, val, errorMsg)=>{
        log.warn(`${message} [${typeName}, ID: ${id ?? 'unknown'}]`);
        if (id === null) return;
        metaErrors.set(`${scope}|${field}`, {
            id,
            typeName,
            field,
            value: val,
            errorMsg: errorMsg || message
        });
    };
    if (!schema || !schema[typeName]) {
        handleError(`missing schema for type ${typeName}`, null, null, null);
        return meta;
    }
    const normalized = schema[typeName].reduce((acc, { name, type, proptype, default: defaultVal })=>{
        let val = meta[name];
        // check empty strings
        if (typeof val === 'string' && val.trim() === '') {
            val = undefined;
        }
        // check null values and apply default
        if (isNil(val)) {
            if (defaultVal !== undefined) {
                acc[name] = defaultVal;
                val = defaultVal;
            }
            return acc;
        }
        const spec = {
            type,
            proptype
        };
        const { isValid, errorMsg } = validateField(val, spec);
        if (!isValid) {
            handleError(`Invalid value: "${val}" for ${name}`, name, val, errorMsg);
            return acc;
        }
        if (proptype === 'percentage') {
            if (typeof val === 'string') {
                let cleanVal = val.trim().replace(',', '.');
                acc[name] = cleanVal.endsWith('%') ? cleanVal : `${cleanVal}%`;
            } else if (typeof val === 'number') {
                acc[name] = `${val}%`;
            }
        } else if (proptype === 'cssdim') {
            acc[name] = typeof val === 'number' ? val : val.trim();
        } else if (type === 'color') {
            acc[name] = val.trim();
        } else if (type === 'int') {
            acc[name] = typeof val === 'string' ? Number(val) : val;
        } else if (type === 'float') {
            acc[name] = typeof val === 'string' ? Number(val) : val;
        } else if (type === 'boolean') {
            if (typeof val === 'boolean') {
                acc[name] = val;
            } else if (typeof val === 'string') {
                const lowerVal = val.toLowerCase().trim();
                if (lowerVal === 'true') {
                    acc[name] = true;
                } else if (lowerVal === 'false') {
                    acc[name] = false;
                } else {
                    handleError(`Invalid boolean: "${val}" for ${name}`, name, val, 'Erwartet boolschen Wert ("true"/"false").');
                }
            } else {
                handleError(`Invalid boolean: "${val}" for ${name}`, name, val, 'Erwartet boolschen Wert ("true"/"false").');
            }
        } else {
            acc[name] = val;
        }
        return acc;
    }, {});
    // log.info(`[${typeName}] Initial props vs normalized:`, {meta, normalized})
    return normalized;
};
export const parseColor = (c)=>{
    if (typeof document === 'undefined') return [
        0,
        0,
        0
    ];
    let cache = parseColor.cache || (parseColor.cache = {});
    if (cache[c]) return cache[c];
    let ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = c;
    ctx.fillRect(0, 0, 1, 1);
    let img = ctx.getImageData(0, 0, 1, 1).data;
    let res = [
        img[0],
        img[1],
        img[2]
    ];
    cache[c] = res;
    return res;
};
export const getContrastColors = (color)=>{
    if (!color) return;
    let [r, g, b] = parseColor(color);
    let yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 140 ? {
        backdrop: 'rgba(30, 30, 30, 0.7)',
        backdropInvert: 'rgba(225, 225, 225, 0.7)',
        content: 'white',
        contentInvert: 'black'
    } : {
        backdrop: 'rgba(225, 225, 225, 0.7)',
        backdropInvert: 'rgba(30, 30, 30, 0.7)',
        content: 'black',
        contentInvert: 'white'
    };
};
